/**
 * Vidéo de formation glissée dans un article de blog, quand elle montre
 * exactement ce que l'article explique (Karim 28/10/2026 : « ajouter parfois
 * des vidéos de nos formations dans les articles quand c'est cohérent »).
 *
 * Une seule vidéo par article, placée juste AVANT l'intertitre `<h2>` indiqué :
 * HAUT dans l'article, juste après le résumé d'ouverture (Karim 28/10 : « trop
 * en bas, les gens la verront pas »), jamais au-dessus du titre. Elle montre
 * le RÉSULTAT pour le donateur ou le participant, pas le tutoriel de réglage. Si
 * l'intertitre n'existe plus dans l'article (texte modifié dans Directus), la
 * vidéo n'est simplement pas affichée.
 */
export interface VideoArticle {
  /** Formation (app/data/formations/catalogue.ts). */
  formation: string;
  /** Module de la formation dont on montre la vidéo. */
  module: string;
  /** Début du texte du `<h2>` AVANT lequel la vidéo s'affiche. */
  avantIntertitre: string;
  /**
   * Titre et résumé propres à l'article, quand ceux du module parlent du
   * réglage alors que l'article montre le résultat (Karim 28/10 : pour les
   * reçus fiscaux, montrer l'envoi automatique, « c'est plus vendeur »).
   */
  titre?: string;
  resume?: string;
}

export const VIDEOS_ARTICLES: Record<string, VideoArticle> = {
  "recu-fiscal-association-cerfa-11580": {
    formation: "recus-fiscaux",
    module: "rendre-les-dons-deductibles",
    avantIntertitre: "Télécharger le modèle officiel",
    titre: "Le reçu fiscal envoyé automatiquement au donateur",
    resume:
      "Le donateur voit ce que son don lui coûte vraiment après la réduction d'impôt, reçoit son reçu fiscal en PDF par e-mail juste après le paiement, et le retrouve à tout moment dans son espace donateur.",
  },
  "carte-membre-association-guide": {
    formation: "adhesion",
    module: "cartes-adherent",
    avantIntertitre: "La carte de membre est-elle obligatoire",
  },
  "free-ticketing-for-associations": {
    formation: "billetterie",
    module: "vendre-les-billets",
    avantIntertitre: "Combien coûte vraiment la billetterie",
    titre: "Ce que vit le participant quand il achète son billet",
    resume:
      "La billetterie publiée et partagée, puis le parcours d'achat côté participant : choix des billets, un nom par billet, paiement en ligne.",
  },
  "donation-tax-reduction": {
    formation: "recus-fiscaux",
    module: "rendre-les-dons-deductibles",
    avantIntertitre: "Comment calculer sa réduction d'impôt",
    titre: "Le donateur voit ce que son don lui coûte vraiment",
    resume:
      "Avant de payer, le donateur voit le coût de son don après la réduction d'impôt. Il reçoit ensuite son reçu fiscal en PDF par e-mail, et le retrouve à tout moment dans son espace donateur.",
  },
  "dons-recurrents-stabilite-financiere": {
    formation: "lien-de-paiement",
    module: "parcours-donateur",
    avantIntertitre: "Pourquoi proposer le don mensuel",
    titre: "Le don mensuel, côté donateur",
    resume:
      "Le donateur ouvre le lien, choisit de donner une fois ou chaque mois, paie en un geste, et reçoit aussitôt son reçu par e-mail.",
  },
  "guide-premiere-cagnotte": {
    formation: "cagnotte",
    module: "vue-donateur-stats-apercu",
    avantIntertitre: "Cagnotte entre proches ou cagnotte d'association",
    titre: "La cagnotte vue par le donateur, et les dons qui arrivent en direct",
    resume:
      "La page telle que la voient vos donateurs, sur ordinateur et sur téléphone, un don qui s'affiche en direct, puis les statistiques de la collecte.",
  },
  "online-membership-for-associations": {
    formation: "adhesion",
    module: "vendre-les-adhesions",
    avantIntertitre: "Pourquoi passer l'adhésion de son association en ligne",
    titre: "Ce que vit le nouvel adhérent quand il adhère en ligne",
    resume:
      "L'adhésion publiée et partagée, puis le parcours côté membre : il remplit sa fiche, choisit ses options, paie en un geste, et votre équipe est prévenue aussitôt.",
  },
  "online-payment-for-associations": {
    formation: "lien-de-paiement",
    module: "parcours-donateur",
    avantIntertitre: "Les cinq façons d'encaisser en ligne",
    titre: "Un paiement en ligne, vu par celui qui paie",
    resume:
      "Il ouvre le lien reçu par WhatsApp, e-mail ou QR code, choisit son montant, paie en un geste, et reçoit aussitôt son reçu par e-mail.",
  },
  "card-terminal-for-associations": {
    formation: "tap-to-pay",
    module: "encaisser",
    avantIntertitre: "Comment une association peut-elle accepter la carte bancaire",
    titre: "Encaisser une carte avec un simple iPhone",
    resume:
      "Le bénévole saisit le montant et présente son téléphone, le donateur approche sa carte : c'est payé en quelques secondes, et son reçu part par e-mail.",
  },

  // ── Révision de tout le blog (28/10/2026) : uniquement quand la vidéo montre
  // exactement ce que vit le donateur, l'adhérent ou le participant dont parle
  // l'article.
  "apple-pay-google-pay-dons-association": {
    formation: "lien-de-paiement",
    module: "parcours-donateur",
    avantIntertitre: "Comment ça marche pour le donateur",
    titre: "Un don par Apple Pay, vu par le donateur",
    resume:
      "Le donateur ouvre le lien, choisit son montant, paie en un geste avec Apple Pay, Google Pay ou sa carte, et reçoit aussitôt son reçu par e-mail.",
  },
  "creer-page-don-qui-convertit": {
    formation: "lien-de-paiement",
    module: "parcours-donateur",
    avantIntertitre: "Pourquoi la page de don est décisive",
    titre: "Une page de don, du clic au reçu",
    resume:
      "Ce que vit le donateur sur son téléphone : le montant à choisir, une fois ou chaque mois, le paiement en un geste et le reçu qui arrive par e-mail.",
  },
  "deduction-fiscale-dons-cultes": {
    formation: "recus-fiscaux",
    module: "rendre-les-dons-deductibles",
    avantIntertitre: "Comment fonctionne la réduction d'impôt de 66 %",
    titre: "Le donateur voit ce que son don lui coûte vraiment",
    resume:
      "Avant de payer, le donateur voit le coût de son don après la réduction d'impôt. Il reçoit ensuite son reçu fiscal en PDF par e-mail, et le retrouve dans son espace donateur.",
  },
  "don-ponctuel-vs-don-recurrent": {
    formation: "lien-de-paiement",
    module: "parcours-donateur",
    avantIntertitre: "Définitions : qu'est-ce qu'un don ponctuel",
    titre: "Donner une fois ou chaque mois, côté donateur",
    resume:
      "Le donateur choisit entre un don unique et un don mensuel, paie en un geste, et reçoit aussitôt son reçu par e-mail.",
  },
  "gerer-cotisations-association-en-ligne": {
    formation: "adhesion",
    module: "vendre-les-adhesions",
    avantIntertitre: "Pourquoi passer les cotisations en ligne",
    titre: "Ce que vit l'adhérent quand il paie sa cotisation en ligne",
    resume:
      "Il remplit sa fiche, choisit ses options, paie en un geste, et votre équipe est prévenue aussitôt.",
  },
  "inscriptions-club-sportif-digitalisees-2026": {
    formation: "adhesion",
    module: "vendre-les-adhesions",
    avantIntertitre: "Les problèmes d'une inscription papier",
    titre: "Une inscription au club, faite en ligne par la famille",
    resume:
      "La famille remplit la fiche, ajoute ses options et ses documents, paie en un geste, et le club est prévenu aussitôt.",
  },
  "multiplier-collectes-recus-fiscaux": {
    formation: "recus-fiscaux",
    module: "rendre-les-dons-deductibles",
    avantIntertitre: "Ce que le reçu fiscal change pour vos donateurs",
    titre: "Le reçu fiscal envoyé automatiquement au donateur",
    resume:
      "Le donateur voit le coût de son don après la réduction d'impôt, reçoit son reçu fiscal en PDF par e-mail juste après le paiement, et le retrouve dans son espace donateur.",
  },
  "organiser-evenement-collecte-reussi": {
    formation: "qr-codes",
    module: "ecran-en-direct",
    avantIntertitre: "Les formats d'événement de collecte",
    titre: "Le QR code sur grand écran, et les dons qui s'affichent en direct",
    resume:
      "Chaque don se retrouve dans votre tableau de bord, et l'écran de la salle s'anime : le montant collecté monte devant tout le monde.",
  },
  "organiser-gala-charite-association": {
    formation: "billetterie",
    module: "vendre-les-billets",
    avantIntertitre: "Un gala de charité, pour quoi faire",
    titre: "Ce que vit l'invité quand il achète sa place",
    resume:
      "La billetterie publiée et partagée, puis le parcours d'achat : choix des billets, un nom par billet, paiement en ligne.",
  },
  "recus-fiscaux-automatiques-donoor": {
    formation: "recus-fiscaux",
    module: "rendre-les-dons-deductibles",
    avantIntertitre: "Pourquoi automatiser les reçus fiscaux",
    titre: "Le reçu fiscal envoyé automatiquement au donateur",
    resume:
      "Le donateur voit le coût de son don après la réduction d'impôt, reçoit son reçu fiscal en PDF par e-mail juste après le paiement, et le retrouve dans son espace donateur.",
  },
  "recus-fiscaux-mosquee-loi-1905": {
    formation: "recus-fiscaux",
    module: "rendre-les-dons-deductibles",
    avantIntertitre: "Les 2 statuts possibles pour une mosquée",
    titre: "Le reçu fiscal envoyé automatiquement au donateur",
    resume:
      "Le donateur voit le coût de son don après la réduction d'impôt, reçoit son reçu fiscal en PDF par e-mail juste après le paiement, et le retrouve dans son espace donateur.",
  },
  "relance-renouvellement-adhesion-association": {
    formation: "adhesion",
    module: "renouvellement",
    avantIntertitre: "Pourquoi les adhérents ne renouvellent pas",
    titre: "Les relances partent seules, l'adhérent renouvelle en un clic",
    resume:
      "Les rappels partent 30, 15 puis 7 jours avant la fin de l'adhésion. L'adhérent reçoit l'e-mail, voit « Expire bientôt » sur sa carte et renouvelle en un clic.",
  },
  "sadaqah-dons-recurrents-guide": {
    formation: "lien-de-paiement",
    module: "parcours-donateur",
    avantIntertitre: "Sadaqah, Sadaqah Jariya, Zakat",
    titre: "Donner chaque mois, côté donateur",
    resume:
      "Le donateur ouvre le lien, choisit de donner une fois ou chaque mois, paie en un geste, et reçoit aussitôt son reçu par e-mail.",
  },
  "tap-to-pay-iphone-association-2026": {
    formation: "tap-to-pay",
    module: "encaisser",
    avantIntertitre: "Comment ça marche",
    titre: "Encaisser une carte avec un simple iPhone",
    resume:
      "Le bénévole saisit le montant et présente son téléphone, le donateur approche sa carte : c'est payé en quelques secondes, et son reçu part par e-mail.",
  },
};

/** Position (index dans le HTML) où insérer la vidéo, ou -1. */
export function positionVideo(html: string, video: VideoArticle): number {
  const echappe = video.avantIntertitre.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return html.search(new RegExp(`<h2[^>]*>\\s*${echappe}`, "i"));
}
